import { Modal, Spin, Button, Row as AntRow, Col } from 'antd';
import { Formik, FieldArray } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalFooter } from '~/components/Modal';
import Row from '~/components/Row';
import FormControl from '~/components/Form/FormControl';
import { FaPlusCircle, FaRegTimesCircle } from 'react-icons/fa';
import * as Yup from 'yup';
import DateComponent from '~/components/DateComponent';
import InputCurrency from '~/components/Form/InputCurrency';
import { Input } from 'formik-antd';
import { createFinancialExpenseItemExpenses } from '~/services/hooks/financialExpenseItemExpenses';
import { useCheckAuth } from '~/components/Can/checkAuth';
import errorHandler from '~/Utils/errorHandler';
import { TitleTag } from './styles';
import { getFinancialExpenses } from '~/services/hooks/financialExpenses';

const initial = {
  financial_expense_item_expenses: [],
};

export default function FinancialExpenseItemExpensesModal({ visible, onClose, isMobile, financialEx }) {
  const { t } = useTranslation();
  const authCheck = useCheckAuth();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(initial);

  const fetchFinancialExpenseItems = async () => {
    setLoading(true);
    try {
      const data = await getFinancialExpenses('', `${financialEx?.id}`);

      setInitialValues(data);
    } catch (error) {
      console.error(error);
      errorHandler(error);
    }
    setLoading(false);
  };

  const saveFinancialExpense = async (values, { setErrors, resetForm }) => {
    setLoading(true);
    try {
      await createFinancialExpenseItemExpenses(values);
      resetForm();
      onClose();
    } catch (error) {
      console.error(error);
      setErrors(errorHandler(error));
    }
    setLoading(false);
  };

  const fluidPermissions = () => {
    return authCheck('@registerExpenses/create');
  };

  const validationSchema = Yup.object().shape({
    financial_expense_item_expenses: Yup.array()
      .of(
        Yup.object().shape({
          spent_at: Yup.date().required('Data do gasto é obrigatória'),
          price: Yup.string().required('Valor é obrigatório'),
          description: Yup.string().nullable(),
        })
      )
      .required(),
  });

  useEffect(() => {
    if (visible && financialEx) {
      fetchFinancialExpenseItems();
    }
  }, [visible, financialEx]);

  return (
    <Formik
      onSubmit={saveFinancialExpense}
      validationSchema={validationSchema}
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
      destroyOnClose
    >
      {({ isSubmitting, submitForm, errors, values, setFieldValue, resetForm }) => (
        <Modal
          title={t('Adicionando despesas') + ' - ' + financialEx?.name}
          onCancel={() => {
            resetForm();
            onClose();
          }}
          style={{ maxWidth: isMobile ? '100%' : '700px' }}
          destroyOnClose
          open={visible}
          loading={isSubmitting}
          centered
          width="1500px"
          footer={<ModalFooter onOk={submitForm} loading={loading || isSubmitting} onCancel={onClose} />}
        >
          <Spin spinning={loading}>
            <FieldArray name="financial_expense_item_expenses">
              {({ push, remove }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {values?.financial_expense_items?.map((fei) => (
                    <div key={fei.id} style={{ border: '1px solid #e8e8e8', padding: '16px', borderRadius: '8px' }}>
                      <Row style={{ display: 'flex', alignItems: 'center', marginBottom: 16, marginLeft: 0 }}>
                        <TitleTag style={{ margin: 0, marginRight: 16 }}>
                          {fei.name}{' '}
                          <Button
                            color="primary"
                            title="Adicionar item de despesa"
                            onClick={() =>
                              push({
                                financial_expense_item_id: fei.id,
                                price: '',
                                spent_at: '',
                                description: '',
                              })
                            }
                            disabled={!fluidPermissions()}
                          >
                            <FaPlusCircle color="green" />
                          </Button>
                        </TitleTag>
                      </Row>
                      {values?.financial_expense_item_expenses?.map((expense, index) => {
                        if (expense.financial_expense_item_id !== fei.id) return null;

                        return (
                          <AntRow
                            key={index}
                            gutter={16}
                            align="middle"
                            style={{ border: '1px solid #d9d9d9', borderRadius: 10, padding: 20, marginBottom: 10 }}
                          >
                            <Col xs={12}>
                              <FormControl
                                cols={{ xs: 24 }}
                                field={`financial_expense_item_expenses.${index}.spent_at`}
                                error={errors?.financial_expense_item_expenses?.[index]?.spent_at}
                                required
                                label={t('fields:feie.spentAt')}
                              >
                                <DateComponent
                                  value={values?.financial_expense_item_expenses?.[index]?.spent_at}
                                  onChange={(date) =>
                                    setFieldValue(`financial_expense_item_expenses.${index}.spent_at`, date)
                                  }
                                  disabled={!fluidPermissions()}
                                />
                              </FormControl>
                            </Col>
                            <Col xs={12}>
                              <FormControl
                                cols={{ xs: 24 }}
                                field={`financial_expense_item_expenses.${index}.price`}
                                error={errors?.financial_expense_item_expenses?.[index]?.price}
                                required
                                label={t('fields:feie.price')}
                              >
                                <InputCurrency
                                  currency="R$"
                                  name={`financial_expense_item_expenses.${index}.price`}
                                  disabled={!fluidPermissions()}
                                />
                              </FormControl>
                            </Col>
                            <Col xs={20}>
                              <FormControl
                                cols={{ xs: 24 }}
                                field={`financial_expense_item_expenses.${index}.description`}
                                error={errors?.financial_expense_item_expenses?.[index]?.description}
                                required
                                label={t('fields:feie.description')}
                              >
                                <Input.TextArea
                                  disabled={!fluidPermissions()}
                                  name={`financial_expense_item_expenses.${index}.description`}
                                />
                              </FormControl>
                            </Col>
                            <Col xs={4}>
                              <Button
                                color="danger"
                                title="Remover item de despesa"
                                onClick={() => remove(index)}
                                style={{ marginTop: 10 }}
                                disabled={!fluidPermissions()}
                              >
                                <FaRegTimesCircle color="red" />
                              </Button>
                            </Col>
                          </AntRow>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </FieldArray>
          </Spin>
        </Modal>
      )}
    </Formik>
  );
}
